// spokenLanguage — which language is a TYPED message in: Afrikaans, English, or
// another (Shona, isiZulu)? (support plan, 27 Oct: "smooth when I switch from
// English to Afrikaans"; Shona test call, 2 Nov.)
//
// On a call she switches the call's own language (speech and voice) with the
// language_detection tool: af, sn and zu (presets in agent.json, since 2 Nov).
// For TYPED messages the panel tells her itself: it sends "Language now: …"
// before the message when the language changes. Typed English/Afrikaans
// switching on the sandbox: 21 of 21 replies right. Only until the caller
// speaks in the call, and never for speech: the recogniser reads the note as
// context (27 Oct, sandbox: Afrikaans speech transcribed as English 4 runs in 5).
//
// It only counts everyday words. Farm and app words (pallet, bins, delivery)
// are on neither list, so they never decide it. Unsure -> null: say nothing.
//
// Shona and isiZulu callers mix in English words ("Ndiri stuck pa screen iyi,
// do I add the bins pano?"), which counted as English and told her "Language
// now: English" (2 Nov). One Shona or isiZulu word written in lower case now
// makes it 'other', and the note tells her to follow their own words.
// Capitalised words never count: they are names (Ndola, Ngidi Farms,
// Zvishavane, Kana the grower). A real Shona message has lower-case Shona too.

const AF = new Set([
  'die', 'en', 'ek', 'jy', 'nie', "'n", 'van', 'wat', 'hoe', 'kan', 'asseblief', 'dankie', 'het', 'om', 'te',
  'vir', 'met', 'sal', 'moet', 'waar', 'hierdie', 'daardie', 'ons', 'hulle', 'nou', 'ja', 'nee', 'goeie',
  'goeiemore', 'goeiemiddag', 'goeienaand', 'verduidelik', 'wys', 'jou', 'sê', 'maak', 'druk', 'tik', 'gaan',
  'baie', 'hallo', 'totsiens', 'weet', 'dit', 'ook', 'maar', 'wil', 'stoor', 'nuwe', 'hoekom', 'wanneer',
  'watter', 'kry', 'vind', 'voeg', 'werk', 'probleem', 'fout', 'by', 'u',
])
const EN = new Set([
  'the', 'and', 'how', 'do', 'i', 'you', 'what', 'can', 'please', 'thanks', 'thank', 'to', 'where', 'this',
  'that', 'it', 'we', 'they', 'yes', 'no', 'show', 'explain', 'does', 'would', 'could', 'should', 'there',
  'with', 'are', 'am', 'a', 'an', 'on', 'for', 'me', 'start', 'print', 'okay', 'ok', 'hello', 'hi', 'bye',
  "won't", "don't", "doesn't", "can't", "isn't", 'not', 'save', 'new', 'help', 'why', 'when', 'which', 'get',
  'go', 'find', 'add', 'have', 'has', 'was', 'will', 'work', 'working', 'stuck', 'error',
])

// Everyday Shona and isiZulu words, and their verb starts (ndi-, nda-, ndo-,
// zv-, ngi-), which no English or Afrikaans word has.
const SN_ZU = new Set([
  'mhoro', 'mhoroi', 'makadii', 'ndatenda', 'ndinotenda', 'ndapota', 'sei', 'zvino', 'pano', 'iyi', 'ichi',
  'kuti', 'ko', 'chii', 'kupi', 'haisi', 'hapana', 'ehe', 'aiwa', 'hongu', 'kana', 'asi', 'ndiro',
  'sawubona', 'yebo', 'cha', 'kanjani', 'ukuthi', 'manje', 'lapha', 'kodwa', 'futhi', 'impela', 'ngoba',
])
const SN_ZU_START = /^(ndi|nda|ndo|zv|ngi)[a-z]/

export type SpokenLanguage = 'af' | 'en' | 'other'

/** Afrikaans, English, another language (Shona, isiZulu), or null when the words do not say. Pure. */
export function spokenLanguage(text: string): SpokenLanguage | null {
  const written = text.replace(/[’`]/g, "'").match(/'n|[a-zA-ZêëéèôöûüîïáàÊËÉÈÔÖÛÜÎÏÁÀ']+/g) ?? []
  if (written.some(w => w === w.toLowerCase() && (SN_ZU.has(w) || SN_ZU_START.test(w)))) return 'other'
  const words = written.map(w => w.toLowerCase())
  let af = 0
  let en = 0
  for (const w of words) {
    if (AF.has(w)) af++
    if (EN.has(w)) en++
  }
  if (af >= 2 && af > en) return 'af'
  if (en >= 2 && en > af) return 'en'
  return null
}

/** The note the panel sends her when the caller's language changes. */
export const languageNote = (lang: SpokenLanguage): string =>
  lang === 'other'
    ? 'Language now: not English or Afrikaans. Answer in the language of their latest message.'
    : `Language now: ${lang === 'af' ? 'Afrikaans' : 'English'}.`
